import type { Profile } from './profiles';

/**
 * Developer profile with realistic tasks and projects
 */
export const developerProfile: Profile = {
  name: 'Developer',
  averageSwitchingFrequencySeconds: 300, // Switch context every ~5 minutes on average
  minMeetingsPerDay: 1,
  meetings: [
    {
      title: 'Daily Standup',
      durationMinutes: 30,
      entries: [
        { tool: 'notion', title: 'Daily Standup Notes - June 17' },
        { tool: 'linear', title: 'Sprint Board - Current Tasks' },
        { tool: 'slack', title: 'Thread: Standup Follow-ups' },
      ],
      aiNotes: {
        concise: 'Daily team sync discussing current sprint tasks.',
        detailed:
          'Participating in daily standup meeting, reviewing current tasks on the sprint board, taking notes, and following up on action items through Slack threads.',
      },
    },
    {
      title: 'Frontend Team Weekly',
      durationMinutes: 60,
      entries: [
        { tool: 'notion', title: 'Frontend Team Meeting Notes - Q3W2' },
        { tool: 'figma', title: 'UI Component Review - June 2024' },
        { tool: 'linear', title: 'Frontend Team Tasks Q3' },
        { tool: 'visual_studio_code', title: 'components/README.md' },
      ],
      aiNotes: {
        concise: 'Weekly frontend team sync reviewing UI components and tasks.',
        detailed:
          'Participating in weekly frontend team meeting, taking notes, reviewing UI components in Figma, discussing Q3 tasks in Linear, and examining component documentation.',
      },
    },
    {
      title: 'TT-4312: Auth System Planning',
      durationMinutes: 60,
      entries: [
        { tool: 'notion', title: 'Auth System Design Document v2' },
        { tool: 'visual_studio_code', title: 'auth/architecture.md' },
        { tool: 'excalidraw', title: 'JWT Refresh Flow Diagram' },
        { tool: 'linear', title: 'TT-4312: Implement JWT refresh token flow' },
        { tool: 'slack', title: 'Thread: Auth System Questions' },
      ],
      aiNotes: {
        concise: 'Planning JWT refresh token authentication system implementation.',
        detailed:
          'Designing authentication system architecture, creating JWT refresh flow diagrams, documenting technical specifications, planning implementation tasks, and discussing system requirements with team members.',
      },
    },
    {
      title: '1:1 with Rachel (Engineering Manager)',
      durationMinutes: 30,
      entries: [
        { tool: 'notion', title: 'Career Development Plan - Q3 2024' },
        { tool: 'notion', title: '1:1 Notes and Action Items - June 17' },
        { tool: 'slack', title: 'Rachel (DM)' },
      ],
      aiNotes: {
        concise: 'Career development discussion with engineering manager.',
        detailed:
          'Meeting with engineering manager to discuss Q3 career development plans, documenting action items and follow-ups, and exchanging messages about professional growth goals.',
      },
    },
    {
      title: 'TT-4280: API Integration Review',
      durationMinutes: 45,
      entries: [
        { tool: 'notion', title: 'API Integration Documentation' },
        { tool: 'visual_studio_code', title: 'services/apiClient.ts' },
        { tool: 'visual_studio_code', title: 'types/api.d.ts' },
        { tool: 'linear', title: 'TT-4280: Update API client for v2 endpoints' },
        { tool: 'postman', title: 'API Endpoint Tests' },
      ],
      aiNotes: {
        concise: 'Reviewing API client updates for v2 endpoints.',
        detailed:
          'Meeting to review API integration development, examining API client implementation code, updating type definitions, tracking progress on Linear tasks, and testing endpoints using Postman.',
      },
    },
    {
      title: 'Q3 Sprint Planning',
      durationMinutes: 90,
      entries: [
        { tool: 'notion', title: 'Sprint Planning Document - Q3' },
        { tool: 'linear', title: 'Sprint Backlog - Q3' },
        { tool: 'figma', title: 'Q3 Roadmap Visual' },
        { tool: 'slack', title: 'sprint-planning' },
      ],
      aiNotes: {
        concise: 'Planning and prioritizing Q3 sprint tasks and deliverables.',
        detailed:
          'Participating in Q3 sprint planning session, reviewing backlog items in Linear, documenting planning decisions in Notion, referring to roadmap visuals, and coordinating with team via Slack.',
      },
    },
    {
      title: 'Sprint Review & Demo',
      durationMinutes: 60,
      entries: [
        { tool: 'visual_studio_code', title: 'demo/AuthDemo.tsx' },
        { tool: 'notion', title: 'Sprint Review Notes' },
        { tool: 'linear', title: 'Completed Tasks - Sprint 24' },
        { tool: 'figma', title: 'Demo Slides - Sprint 24' },
      ],
      aiNotes: {
        concise: 'Demonstrating authentication features completed in sprint.',
        detailed:
          'Presenting completed sprint work to team, demonstrating new authentication features, reviewing sprint accomplishments, taking notes on feedback, and discussing completed Linear tasks.',
      },
    },
    {
      title: 'TT-3890: Dashboard Performance Review',
      durationMinutes: 45,
      entries: [
        { tool: 'visual_studio_code', title: 'components/Dashboard/PerformanceChart.tsx' },
        { tool: 'notion', title: 'Performance Optimization Notes' },
        { tool: 'linear', title: 'TT-3890: Fix dashboard rendering performance' },
        { tool: 'slack', title: 'Thread: Performance Improvement Ideas' },
      ],
      aiNotes: {
        concise: 'Analyzing dashboard performance issues and optimization strategies.',
        detailed:
          'Reviewing dashboard component performance bottlenecks, discussing optimization techniques, documenting findings in performance notes, and gathering improvement suggestions through Slack discussion.',
      },
    },
    {
      title: 'Code Review Session',
      durationMinutes: 60,
      entries: [
        { tool: 'github', title: 'PR #423: Implement JWT refresh token flow' },
        { tool: 'visual_studio_code', title: 'auth/UserAuthentication.ts' },
        { tool: 'slack', title: 'code-reviews' },
        { tool: 'notion', title: 'Code Review Guidelines' },
      ],
      aiNotes: {
        concise: 'Reviewing JWT refresh token implementation PR with team.',
        detailed:
          'Conducting code review of JWT authentication implementation, examining authentication code in VS Code, following team code review guidelines, and discussing changes through Slack channels.',
      },
    },
    {
      title: 'Engineering All-Hands',
      durationMinutes: 60,
      entries: [],
      aiNotes: {
        concise: 'Attending engineering department company-wide meeting.',
        detailed:
          'Participating in engineering department all-hands meeting, receiving updates on company initiatives, department goals, and ongoing projects.',
      },
    },
  ],
  sessions: [
    {
      name: 'Feature Development - Authentication System',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'auth/UserAuthentication.ts' },
        { tool: 'visual_studio_code', title: 'auth/AuthContext.tsx' },
        { tool: 'visual_studio_code', title: 'auth/useAuth.ts' },
        { tool: 'visual_studio_code', title: 'components/LoginForm.tsx' },
        { tool: 'visual_studio_code', title: 'services/authService.ts' },
        { tool: 'visual_studio_code', title: 'types/auth.d.ts' },
        { tool: 'iTerm2', title: 'npm test -- --watch auth' },
        { tool: 'iTerm2', title: 'git status' },
        { tool: 'github', title: 'PR #423: Implement JWT refresh token flow' },
        { tool: 'linear', title: 'TT-4312: Implement JWT refresh token flow' },
        { url: 'https://github.com', title: 'GitHub - JWT authentication best practices' },
        { url: 'https://jwt.io', title: 'JWT.io - Debugger' },
      ],
      aiNotes: {
        concise: 'Developing authentication system with JWT refresh token flow.',
        detailed:
          'Working on the authentication system implementation in VSCode, focusing on JWT refresh token flow across multiple files including UserAuthentication.ts, AuthContext.tsx, and LoginForm.tsx. Running tests, checking git status, and referencing JWT documentation.',
      },
    },
    {
      name: 'Bug Fix - Dashboard Performance',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'components/Dashboard/PerformanceChart.tsx' },
        { tool: 'visual_studio_code', title: 'utils/performanceMetrics.ts' },
        { tool: 'visual_studio_code', title: 'hooks/useDashboardData.ts' },
        { tool: 'visual_studio_code', title: 'services/metricsService.ts' },
        { tool: 'iTerm2', title: 'npm run profile:dashboard' },
        { tool: 'iTerm2', title: 'git diff components/Dashboard/' },
        { tool: 'github', title: 'Issue #271: Dashboard performance degradation' },
        { tool: 'linear', title: 'TT-3890: Fix dashboard rendering performance' },
        { url: 'https://web.dev', title: 'Web.dev - Core Web Vitals optimization' },
        { url: 'https://reactjs.org', title: 'React docs - Profiler API' },
        { url: 'https://youtube.com', title: 'YouTube - React Performance with memo and useMemo' },
      ],
      aiNotes: {
        concise: 'Fixing dashboard performance issues and optimizing rendering.',
        detailed:
          'Debugging and fixing performance issues in the dashboard components, focusing on PerformanceChart.tsx and related files. Running performance profiling, analyzing code differences, and researching React optimization techniques with memo and useMemo.',
      },
    },
    {
      name: 'Code Review',
      predominantTool: 'github',
      entries: [
        { tool: 'github', title: 'PR #419: Migrate API endpoints to v2' },
        { tool: 'github', title: 'PR #422: Refactor notification system' },
        { tool: 'github', title: 'PR #408: Add team management features' },
        { tool: 'visual_studio_code', title: 'api/endpoints.ts' },
        { tool: 'visual_studio_code', title: 'services/NotificationService.ts' },
        { tool: 'visual_studio_code', title: 'components/TeamManagement/TeamForm.tsx' },
        { tool: 'iTerm2', title: 'git checkout feature/notification-refactor' },
        { tool: 'iTerm2', title: 'npm test services/notifications' },
        { url: 'https://github.com', title: 'GitHub - Pull request review guidelines' },
        {
          url: 'https://stackoverflow.com',
          title: 'Stack Overflow - Efficient code review practices',
        },
      ],
      aiNotes: {
        concise:
          'Reviewing pull requests for API migration, notification system, and team features.',
        detailed:
          'Performing code reviews on GitHub for multiple PRs including API endpoint migration to v2, notification system refactoring, and team management features. Checking out branches, examining code files, and running tests on the notification service.',
      },
    },
    {
      name: 'Project Planning',
      predominantTool: 'linear',
      entries: [
        { tool: 'linear', title: 'Q3 Roadmap: Analytics Dashboard' },
        { tool: 'linear', title: 'TT-4350: Implement user activity tracking' },
        { tool: 'linear', title: 'TT-4427: Improve error handling in API client' },
        { tool: 'notion', title: 'Architecture: Data Flow Diagrams' },
        { tool: 'notion', title: 'Tech Spec: Analytics Service' },
        { tool: 'slack', title: 'tech-planning' },
        { tool: 'slack', title: 'Sarah (DM)' },
        { url: 'https://miro.com', title: 'Miro - Project roadmap visualization' },
        { url: 'https://productplan.com', title: 'ProductPlan - Q3 product roadmap' },
        { url: 'https://atlassian.com', title: 'Atlassian - Agile project management guide' },
      ],
      aiNotes: {
        concise: 'Planning analytics dashboard features and API improvements.',
        detailed:
          'Planning Q3 roadmap for analytics dashboard, user activity tracking, and API error handling. Creating data flow diagrams, tech specs, and collaborating with team through Slack and project visualization tools.',
      },
    },
    {
      name: 'Team Communication',
      predominantTool: 'slack',
      maxLengthMinutes: 25,
      entries: [
        { tool: 'slack', title: 'dev-team' },
        { tool: 'slack', title: 'frontend' },
        { tool: 'slack', title: 'Michael (DM)' },
        { tool: 'slack', title: 'Jessica (DM)' },
        { tool: 'gmail', title: 'Email: Weekly Team Update' },
        { tool: 'notion', title: 'Team Communication Guidelines' },
        { url: 'https://calendar.google.com', title: 'Google Calendar - Team availability' },
        { url: 'https://slack.com', title: 'Slack - Workspace settings' },
      ],
      aiNotes: {
        concise: 'Coordinating with team members via chat and email.',
        detailed:
          'Communicating with team members through Slack channels and direct messages, reviewing weekly updates via email, and checking team availability on Google Calendar.',
      },
    },
    {
      name: 'API Integration',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'services/apiClient.ts' },
        { tool: 'visual_studio_code', title: 'hooks/useApi.ts' },
        { tool: 'visual_studio_code', title: 'utils/requestUtils.ts' },
        { tool: 'visual_studio_code', title: 'types/api.d.ts' },
        { tool: 'iTerm2', title: 'curl -X GET https://api.example.com/v2/users' },
        { tool: 'iTerm2', title: 'npm test services/apiClient' },
        { tool: 'linear', title: 'TT-4280: Update API client for v2 endpoints' },
        { tool: 'postman', title: 'API Testing - V2 Endpoints' },
        { url: 'https://npmjs.com', title: 'npm - react-query package' },
        { url: 'https://tanstack.com', title: 'TanStack Query Documentation' },
        { url: 'https://swagger.io', title: 'Swagger - API Schema Documentation' },
        { url: 'https://developer.mozilla.org', title: 'MDN - Fetch API Reference' },
      ],
      aiNotes: {
        concise: 'Implementing API client updates for v2 endpoints.',
        detailed:
          'Working on API client integration in VSCode, developing apiClient.ts, useApi hooks, and type definitions. Testing API endpoints with curl and Postman, referencing TanStack Query documentation and Swagger API schemas.',
      },
    },
    {
      name: 'TypeScript Learning',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'types/advanced-types.ts' },
        { tool: 'visual_studio_code', title: 'utils/type-helpers.ts' },
        { url: 'https://typescriptlang.org', title: 'TypeScript - Advanced Types' },
        {
          url: 'https://stackoverflow.com',
          title: 'Stack Overflow - TypeScript generics question',
        },
      ],
      aiNotes: {
        concise: 'Studying advanced TypeScript type concepts.',
        detailed:
          'Learning advanced TypeScript features, focusing on complex type definitions and helper functions. Researching TypeScript documentation and Stack Overflow posts about generics implementation.',
      },
    },
    {
      name: 'React Component Optimization',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'components/DataTable.tsx' },
        { tool: 'visual_studio_code', title: 'hooks/useOptimizedQuery.ts' },
        { tool: 'iTerm2', title: 'npm run test:performance' },
        {
          url: 'https://stackoverflow.com',
          title: 'Stack Overflow - useEffect dependency warnings',
        },
        {
          url: 'https://stackoverflow.com',
          title: 'Stack Overflow - React performance optimization techniques',
        },
      ],
      aiNotes: {
        concise: 'Optimizing DataTable component performance.',
        detailed:
          'Improving performance of the DataTable component and creating optimized query hooks. Running performance tests and researching React optimization techniques for useEffect dependencies.',
      },
    },
    {
      name: 'Web API Research',
      predominantTool: 'google_chrome',
      entries: [
        { url: 'https://developer.mozilla.org', title: 'MDN - Intersection Observer API' },
        { url: 'https://developer.mozilla.org', title: 'MDN - Service Workers API' },
        { url: 'https://developer.mozilla.org', title: 'MDN - Cache Storage API' },
        {
          url: 'https://stackoverflow.com',
          title: 'Stack Overflow - Service worker caching strategies',
        },
        { tool: 'visual_studio_code', title: 'utils/intersectionObserver.ts' },
      ],
      aiNotes: {
        concise: 'Researching modern web API implementations.',
        detailed:
          'Studying modern web APIs including Intersection Observer, Service Workers, and Cache Storage. Implementing an intersection observer utility and researching service worker caching strategies.',
      },
    },
    {
      name: 'Authentication Research',
      predominantTool: 'google_chrome',
      entries: [
        { url: 'https://google.com', title: 'Google search: NextJS API routes authentication' },
        { url: 'https://auth0.com', title: 'Auth0 - Modern Authentication Patterns' },
        {
          url: 'https://stackoverflow.com',
          title: 'Stack Overflow - NextJS API route auth strategies',
        },
        { url: 'https://jwt.io', title: 'JWT.io - Token structure analysis' },
        { tool: 'notion', title: 'Authentication Research Notes' },
      ],
      aiNotes: {
        concise: 'Researching authentication patterns for NextJS API routes.',
        detailed:
          'Researching authentication methods for NextJS API routes, studying modern auth patterns from Auth0, analyzing JWT token structures, and documenting findings in authentication research notes.',
      },
    },
    {
      name: 'Diagramming',
      predominantTool: 'excalidraw',
      entries: [
        { tool: 'excalidraw', title: 'Data Flow Diagram - New Feature' },
        { tool: 'excalidraw', title: 'Component Hierarchy - Dashboard' },
        { url: 'https://excalidraw.com', title: 'Excalidraw - Authentication flow diagram' },
        { url: 'https://google.com', title: 'Google search: System design diagram examples' },
        { tool: 'slack', title: 'Thread: Architecture Discussion' },
      ],
      aiNotes: {
        concise: 'Creating technical diagrams for feature architecture.',
        detailed:
          'Creating data flow and component hierarchy diagrams using Excalidraw, designing authentication flow diagrams, and discussing architecture with team members through Slack.',
      },
    },
    {
      name: 'CI/CD Pipeline Configuration',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: '.github/workflows/main.yml' },
        { tool: 'visual_studio_code', title: 'scripts/deploy.sh' },
        { tool: 'visual_studio_code', title: '.github/workflows/test.yml' },
        { tool: 'github', title: 'Repository Actions Configuration' },
        { tool: 'github', title: 'CI/CD Pipeline Runs' },
        { tool: 'iTerm2', title: 'npm run test:ci' },
        { tool: 'slack', title: 'ci-cd-alerts' },
        { url: 'https://docs.github.com', title: 'GitHub Actions Documentation' },
        {
          url: 'https://stackoverflow.com',
          title: 'Stack Overflow - GitHub Actions workflow syntax',
        },
        { url: 'https://circleci.com', title: 'CircleCI - Pipeline configuration reference' },
      ],
      aiNotes: {
        concise: 'Setting up GitHub Actions workflows for CI/CD.',
        detailed:
          'Configuring GitHub Actions workflows for continuous integration and deployment, writing deployment scripts, running CI tests, and monitoring alerts through Slack. Referencing GitHub Actions and CircleCI documentation.',
      },
    },
    {
      name: 'Database Schema Design',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'prisma/schema.prisma' },
        { tool: 'visual_studio_code', title: 'server/models/database.ts' },
        {
          tool: 'visual_studio_code',
          title: 'server/migrations/202406121523_add_user_preferences.ts',
        },
        { tool: 'iTerm2', title: 'npx prisma migrate dev' },
        { tool: 'iTerm2', title: 'npx prisma studio' },
        { tool: 'linear', title: 'TT-4251: Update user preferences schema' },
        { url: 'https://prisma.io/docs', title: 'Prisma Documentation - Schema definition' },
        { url: 'https://dbdiagram.io', title: 'DB Diagram - User system schema visualization' },
        {
          url: 'https://stackoverflow.com',
          title: 'Stack Overflow - Database relationship modeling',
        },
      ],
      aiNotes: {
        concise: 'Developing database schema for user preferences.',
        detailed:
          'Creating and updating Prisma schema for database models, implementing user preferences migration, running Prisma commands for migrations and visualization, and researching database relationship modeling.',
      },
    },
    {
      name: 'Performance Profiling',
      predominantTool: 'google_chrome',
      entries: [
        { tool: 'google_chrome', title: 'Chrome DevTools - Performance tab' },
        { tool: 'google_chrome', title: 'Chrome DevTools - Memory tab' },
        { tool: 'visual_studio_code', title: 'components/DataTable/TableRow.tsx' },
        { tool: 'visual_studio_code', title: 'utils/memoization.ts' },
        { tool: 'iTerm2', title: 'npm run analyze-bundle' },
        { tool: 'iTerm2', title: 'node --inspect server.js' },
        { tool: 'linear', title: 'TT-4118: Optimize table rendering performance' },
        { url: 'https://web.dev/performance', title: 'Web.dev - Performance optimization guide' },
        { url: 'https://reactjs.org/docs/profiler.html', title: 'React Profiler Documentation' },
        { url: 'https://developer.chrome.com', title: 'Chrome DevTools Performance Analysis' },
      ],
      aiNotes: {
        concise: 'Profiling and optimizing table rendering performance.',
        detailed:
          'Using Chrome DevTools to analyze performance and memory usage, optimizing DataTable components, running bundle analysis, and implementing memoization techniques for improved rendering efficiency.',
      },
    },
    {
      name: 'Microservices Architecture Research',
      predominantTool: 'google_chrome',
      entries: [
        { url: 'https://microservices.io', title: 'Microservices architecture patterns' },
        { url: 'https://martinfowler.com', title: 'Martin Fowler - Microservices guide' },
        { url: 'https://kubernetes.io', title: 'Kubernetes documentation' },
        { tool: 'visual_studio_code', title: 'architecture/microservices.md' },
        { tool: 'notion', title: 'Microservices Architecture Notes' },
      ],
      aiNotes: {
        concise: 'Researching microservices patterns and best practices.',
        detailed:
          'Studying microservices architecture patterns and implementation guides, reviewing Kubernetes documentation for container orchestration, and documenting findings in architecture notes.',
      },
    },
    {
      name: 'Team Mentoring Session',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'junior-tasks/auth-improvements.md' },
        { tool: 'visual_studio_code', title: 'demos/authentication-flow.tsx' },
        { tool: 'slack', title: 'mentoring-program' },
        { tool: 'notion', title: 'Junior Developer Onboarding Guide' },
      ],
      aiNotes: {
        concise: 'Mentoring junior developers on authentication flows.',
        detailed:
          'Conducting mentoring session for junior developers, focusing on authentication improvements, creating demonstration components, and reviewing onboarding documentation for new team members.',
      },
    },
    {
      name: 'Frontend Refactoring',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'components/UserProfile/UserProfile.tsx' },
        { tool: 'visual_studio_code', title: 'components/UserProfile/UserAvatar.tsx' },
        { tool: 'visual_studio_code', title: 'components/UserProfile/UserSettings.tsx' },
        { tool: 'visual_studio_code', title: 'styles/components/profile.scss' },
        { tool: 'iTerm2', title: 'npm run test:watch -- UserProfile' },
        { tool: 'linear', title: 'TT-4387: Refactor UserProfile component' },
      ],
      aiNotes: {
        concise: 'Refactoring UserProfile component structure.',
        detailed:
          'Restructuring UserProfile component architecture, separating concerns into subcomponents, updating styles, and running component tests to validate changes during the refactoring process.',
      },
    },
    {
      name: 'DevOps Improvement',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'docker-compose.yml' },
        { tool: 'visual_studio_code', title: 'Dockerfile.dev' },
        { tool: 'visual_studio_code', title: 'scripts/ci-build.sh' },
        { tool: 'iTerm2', title: 'docker-compose up -d' },
        { tool: 'iTerm2', title: 'docker logs -f app' },
        { tool: 'linear', title: 'TT-4401: Improve local development environment' },
      ],
      aiNotes: {
        concise: 'Enhancing local development environment with Docker.',
        detailed:
          'Improving local development environment by configuring Docker containers, writing deployment scripts, running and monitoring containers, and tracking progress through Linear task management.',
      },
    },
    {
      name: 'A/B Testing Implementation',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'utils/abTesting.ts' },
        { tool: 'visual_studio_code', title: 'components/Experiment/ABTest.tsx' },
        { tool: 'visual_studio_code', title: 'components/Experiment/Variant.tsx' },
        { tool: 'visual_studio_code', title: 'hooks/useExperiment.ts' },
        { tool: 'notion', title: 'A/B Testing Strategy - Q3 2024' },
        { tool: 'linear', title: 'TT-4325: Implement A/B testing framework' },
      ],
      aiNotes: {
        concise: 'Building A/B testing framework for experiments.',
        detailed:
          'Implementing A/B testing framework with experiment components, variants handling, custom hooks for experiment management, and documenting the testing strategy for Q3 implementation.',
      },
    },
    {
      name: 'State Management Improvement',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'store/index.ts' },
        { tool: 'visual_studio_code', title: 'store/user/actions.ts' },
        { tool: 'visual_studio_code', title: 'store/user/reducers.ts' },
        { tool: 'visual_studio_code', title: 'store/user/selectors.ts' },
        { tool: 'visual_studio_code', title: 'hooks/useAppSelector.ts' },
        { tool: 'linear', title: 'TT-4290: Improve state management architecture' },
      ],
      aiNotes: {
        concise: 'Enhancing Redux store architecture and selectors.',
        detailed:
          'Improving state management architecture by refactoring Redux store organization, updating user actions, reducers, and selectors, and creating optimized hooks for state access.',
      },
    },
    {
      name: 'API Error Handling',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'utils/errorHandling.ts' },
        { tool: 'visual_studio_code', title: 'components/ErrorBoundary.tsx' },
        { tool: 'visual_studio_code', title: 'components/ErrorMessage.tsx' },
        { tool: 'visual_studio_code', title: 'hooks/useErrorHandler.ts' },
        { tool: 'iTerm2', title: 'npm test -- ErrorBoundary' },
        { tool: 'linear', title: 'TT-4315: Improve API error handling' },
      ],
      aiNotes: {
        concise: 'Implementing robust error handling for API operations.',
        detailed:
          'Developing comprehensive error handling utilities for API operations, creating ErrorBoundary and ErrorMessage components, implementing custom error handling hooks, and testing error boundary implementation.',
      },
    },
    {
      name: 'Accessibility Improvement',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'components/common/Button.tsx' },
        { tool: 'visual_studio_code', title: 'components/common/FormField.tsx' },
        { tool: 'visual_studio_code', title: 'utils/accessibility.ts' },
        { tool: 'visual_studio_code', title: 'styles/accessibility.scss' },
        { tool: 'google_chrome', title: 'Chrome DevTools - Accessibility audit' },
        { tool: 'linear', title: 'TT-4399: Improve WCAG compliance' },
      ],
      aiNotes: {
        concise: 'Enhancing WCAG compliance in UI components.',
        detailed:
          'Improving accessibility of common UI components like buttons and form fields, writing accessibility utility functions, conducting accessibility audits in Chrome DevTools, and tracking WCAG compliance tasks.',
      },
    },
    {
      name: 'Unit Testing',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'tests/components/UserProfile.test.tsx' },
        { tool: 'visual_studio_code', title: 'tests/utils/auth.test.ts' },
        { tool: 'visual_studio_code', title: 'tests/hooks/useAuth.test.ts' },
        { tool: 'iTerm2', title: 'npm test -- --coverage' },
        { tool: 'github', title: 'PR #429: Increase test coverage' },
        { tool: 'linear', title: 'TT-4330: Improve test coverage' },
      ],
      aiNotes: {
        concise: 'Increasing test coverage for components and utilities.',
        detailed:
          'Writing unit tests for UserProfile components, authentication utilities, and custom hooks. Running test coverage reports and submitting pull requests to improve overall test coverage quality.',
      },
    },
    {
      name: 'Performance Optimization',
      predominantTool: 'visual_studio_code',
      entries: [
        { tool: 'visual_studio_code', title: 'webpack.config.js' },
        { tool: 'visual_studio_code', title: 'utils/performance.ts' },
        { tool: 'visual_studio_code', title: 'components/LazyImage.tsx' },
        { tool: 'iTerm2', title: 'npm run analyze-bundle' },
        { tool: 'google_chrome', title: 'Chrome DevTools - Performance tab' },
        { tool: 'linear', title: 'TT-4318: Optimize bundle size' },
      ],
      aiNotes: {
        concise: 'Optimizing application bundle size and loading performance.',
        detailed:
          'Configuring webpack for optimized builds, implementing performance utilities, creating lazy-loaded image components, analyzing bundle size, and optimizing application load performance.',
      },
    },
  ],
};
